
export const exportToHtml = (elementId: string, title: string, subtitle: string = "Generated via AdNexus AI") => {
  const contentElement = document.getElementById(elementId);
  if (!contentElement) {
    console.error(`Element with id ${elementId} not found`);
    return;
  }

  const reportContent = contentElement.innerHTML;
  const date = new Date().toLocaleString();

  const fullHtml = `
    <!DOCTYPE html>
    <html lang="en">
      <head>
        <meta charset="UTF-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <title>${title} - AdNexus Report</title>
        <script src="https://cdn.tailwindcss.com"></script>
        <link href="https://fonts.googleapis.com/icon?family=Material+Icons" rel="stylesheet" />
        <style>
          body { 
            background-color: #0f172a; 
            color: #f8fafc; 
            padding: 40px; 
            font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif;
          }
          .report-header {
            border-bottom: 1px solid #334155;
            padding-bottom: 20px;
            margin-bottom: 30px;
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
          }
          .glass-panel { 
            background: #1e293b; 
            border: 1px solid #334155; 
            border-radius: 0.75rem; 
            padding: 1.25rem; 
            margin-bottom: 1.5rem;
            box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
          }
          /* Ensure Recharts are visible */
          .recharts-wrapper { width: 100% !important; height: auto !important; min-height: 250px; }
          
          /* Grid Layout Preservation */
          .grid-container {
             display: grid;
             grid-template-columns: repeat(2, minmax(0, 1fr));
             gap: 1.5rem;
          }
          @media (max-width: 768px) {
             .grid-container { grid-template-columns: repeat(1, minmax(0, 1fr)); }
          }
          
          /* Typography adjustments for static report */
          h3 { font-size: 1.125rem; font-weight: 600; color: #fff; margin-bottom: 1rem; display: flex; align-items: center; gap: 0.5rem; }
          table { width: 100%; font-size: 0.875rem; text-align: left; color: #94a3b8; }
          th { text-transform: uppercase; font-size: 0.75rem; color: #e2e8f0; padding: 0.5rem 0.75rem; background-color: #1e293b; }
          td { padding: 0.5rem 0.75rem; border-bottom: 1px solid #334155; }
          tr:last-child td { border-bottom: none; }
          
          .material-icons { font-size: 1.25rem; }
        </style>
      </head>
      <body>
        <div class="report-header">
          <div>
            <h1 class="text-3xl font-bold text-white mb-2">${title}</h1>
            <p class="text-slate-400">${subtitle}</p>
          </div>
          <div class="text-right">
            <div class="flex items-center justify-end gap-2 mb-1">
                <div class="w-6 h-6 bg-indigo-600 rounded flex items-center justify-center font-bold text-white text-xs">A</div>
                <span class="font-bold text-xl text-white">AdNexus AI</span>
            </div>
            <p class="text-sm text-slate-500">${date}</p>
          </div>
        </div>
        
        <div class="grid-container">
          ${reportContent}
        </div>
        
        <div class="mt-10 pt-6 border-t border-slate-800 text-center text-slate-600 text-sm">
            &copy; ${new Date().getFullYear()} InnovMar Inc. Confidential Report.
        </div>
      </body>
    </html>
  `;

  const blob = new Blob([fullHtml], { type: 'text/html' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${title.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0, 10)}.html`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Professional branded PDF export for Lumi Tours reporting.
 * 
 * Renders the target element using html2canvas at high DPI, then lays it out
 * across A4 pages with a branded header (logo + report title + date) and 
 * footer (copyright + page numbers) on every page.
 */
export const exportToPdf = async (elementId: string, title: string, aiSummary?: string) => {
  const contentElement = document.getElementById(elementId);
  if (!contentElement) {
    console.error(`Element with id ${elementId} not found`);
    return;
  }

  try {
    const html2canvas = (await import('html2canvas')).default;
    const { jsPDF } = await import('jspdf');

    // ── A4 dimensions ──
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const pageWidth = pdf.internal.pageSize.getWidth();   // 210
    const pageHeight = pdf.internal.pageSize.getHeight();  // 297
    const margin = 12;
    const contentWidth = pageWidth - margin * 2;

    // ── Brand colors ──
    const bgColor: [number, number, number] = [15, 23, 42];    // #0f172a slate-900
    const headerBg: [number, number, number] = [30, 41, 59];   // #1e293b slate-800
    const tealAccent: [number, number, number] = [20, 184, 166]; // #14b8a6 teal-500
    const white: [number, number, number] = [255, 255, 255];
    const slate400: [number, number, number] = [148, 163, 184]; // #94a3b8
    const slate600: [number, number, number] = [71, 85, 105];   // #475569

    const reportDate = new Date().toLocaleDateString('sv-SE', {
      year: 'numeric', month: 'long', day: 'numeric'
    });

    // ── Draw branded header ──
    const drawHeader = (doc: any) => {
      // Header background bar
      doc.setFillColor(...headerBg);
      doc.rect(0, 0, pageWidth, 22, 'F');

      // "LUMI" in white
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.setTextColor(...white);
      doc.text('LUMI', margin, 14);

      // "TOURS" in teal
      const lumiWidth = doc.getTextWidth('LUMI');
      doc.setTextColor(...tealAccent);
      doc.text('TOURS', margin + lumiWidth + 1, 14);

      // Report title — right-aligned
      doc.setFontSize(9);
      doc.setTextColor(...slate400);
      doc.setFont('helvetica', 'normal');
      doc.text(title, pageWidth - margin, 10, { align: 'right' });

      // Date — right-aligned below title
      doc.setFontSize(7);
      doc.setTextColor(...slate600);
      doc.text(reportDate, pageWidth - margin, 15, { align: 'right' });

      // URL below logo
      doc.setFontSize(7);
      doc.setTextColor(...slate600);
      doc.text('lumitours.se', margin, 19);

      // Subtle divider line
      doc.setDrawColor(...tealAccent);
      doc.setLineWidth(0.4);
      doc.line(margin, 22, pageWidth - margin, 22);
    };

    // ── Draw branded footer ──
    const drawFooter = (doc: any, pageNum: number, totalPages: number) => {
      const footerY = pageHeight - 8;
      doc.setFontSize(6);
      doc.setTextColor(...slate600);
      doc.setFont('helvetica', 'normal');
      doc.text(`© ${new Date().getFullYear()} InnovMar Inc. • lumitours.se • Confidential`, margin, footerY);
      doc.text(`${pageNum} / ${totalPages}`, pageWidth - margin, footerY, { align: 'right' });

      // Subtle top line for footer
      doc.setDrawColor(51, 65, 85); // slate-700
      doc.setLineWidth(0.2);
      doc.line(margin, footerY - 3, pageWidth - margin, footerY - 3);
    };

    // ── Capture content ──
    const canvas = await html2canvas(contentElement, {
      scale: 2,
      backgroundColor: '#0f172a',
      useCORS: true,
      logging: false,
      windowWidth: contentElement.scrollWidth,
      windowHeight: contentElement.scrollHeight,
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.92);

    // ── Layout calculation ──
    const headerHeight = 26;  // mm reserved for header
    const footerHeight = 12;  // mm reserved for footer
    const aiSummaryHeight = aiSummary ? Math.min(Math.ceil(aiSummary.length / 120) * 4 + 12, 50) : 0;
    const drawableHeight = pageHeight - headerHeight - footerHeight;
    const firstPageDrawable = drawableHeight - aiSummaryHeight;

    // Scale image to fit content width
    const imgWidth = contentWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    // Calculate total pages needed
    let remainingHeight = imgHeight;
    let pageCount = 1;
    remainingHeight -= firstPageDrawable;
    while (remainingHeight > 0) {
      pageCount++;
      remainingHeight -= drawableHeight;
    }

    // ── Page 1 ──
    // Full-page dark background
    pdf.setFillColor(...bgColor);
    pdf.rect(0, 0, pageWidth, pageHeight, 'F');

    drawHeader(pdf);

    let yOffset = headerHeight;

    // Optional AI summary text (rendered natively for crisp text)
    if (aiSummary && aiSummary.trim()) {
      pdf.setFillColor(...headerBg);
      pdf.roundedRect(margin, yOffset, contentWidth, aiSummaryHeight - 2, 2, 2, 'F');

      // AI badge
      pdf.setFontSize(6);
      pdf.setTextColor(...tealAccent);
      pdf.setFont('helvetica', 'bold');
      pdf.text('✦ AI INSIGHTS', margin + 4, yOffset + 5);

      // Summary text
      pdf.setFontSize(7);
      pdf.setTextColor(...slate400);
      pdf.setFont('helvetica', 'normal');
      const lines = pdf.splitTextToSize(aiSummary, contentWidth - 8);
      pdf.text(lines.slice(0, 8), margin + 4, yOffset + 10);

      yOffset += aiSummaryHeight;
    }

    // Draw content image — first page portion
    const firstSliceHeight = firstPageDrawable;
    pdf.addImage(imgData, 'JPEG', margin, yOffset, imgWidth, imgHeight);

    // Clip the image to only show what fits on this page (by overdrawing bg on footer area)
    pdf.setFillColor(...bgColor);
    pdf.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');

    drawFooter(pdf, 1, pageCount);

    // ── Subsequent pages ──
    let heightConsumed = firstSliceHeight;
    for (let page = 2; page <= pageCount; page++) {
      pdf.addPage();

      // Dark background
      pdf.setFillColor(...bgColor);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');

      drawHeader(pdf);

      // Continue drawing the image, offset upward so the next portion appears
      const imgYPos = headerHeight - heightConsumed;
      pdf.addImage(imgData, 'JPEG', margin, imgYPos, imgWidth, imgHeight);

      // Overdraw header and footer areas to clip
      pdf.setFillColor(...bgColor);
      pdf.rect(0, 0, pageWidth, headerHeight, 'F');
      drawHeader(pdf); // Redraw header on top of clip

      pdf.setFillColor(...bgColor);
      pdf.rect(0, pageHeight - footerHeight, pageWidth, footerHeight, 'F');
      drawFooter(pdf, page, pageCount);

      heightConsumed += drawableHeight;
    }

    // ── Save ──
    const sanitizedTitle = title.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_-]/g, '');
    pdf.save(`LumiTours_${sanitizedTitle}_${new Date().toISOString().slice(0, 10)}.pdf`);
  } catch (err) {
    console.error('PDF export error:', err);
  }
};
